import React, { forwardRef } from "react";
import axios from "axios";
import MultiEntityMentionsEditor, {
  EntitySuggestion,
  MultiEntityMentionsEditorRef,
} from "../MultiEntityMentionsEditor";
import { getAuthHeaders } from "../../utils/auth";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4001";

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  organizationId: string;
  apiEndpoint?: string; // Override the default search endpoint if needed
}

/**
 * Reusable markdown editor component with entity mention support.
 * This wraps the MultiEntityMentionsEditor with standard entity search functionality.
 */
const MarkdownEditor = forwardRef<
  MultiEntityMentionsEditorRef,
  MarkdownEditorProps
>(
  (
    { value, onChange, placeholder, disabled, organizationId, apiEndpoint },
    ref,
  ) => {
    const handleSearchEntities = async (
      entityType: string,
      query: string,
    ): Promise<EntitySuggestion[]> => {
      try {
        // Use the provided endpoint or default to account-plans entity search endpoint
        // This endpoint supports all entity types including users
        const endpoint = apiEndpoint || "/api/v1/account-plans/entities/search";

        const response = await axios.get(`${API_URL}${endpoint}`, {
          params: {
            entity_type: entityType,
            query: query || "",
            limit: 15,
            organization_id: organizationId,
          },
          headers: getAuthHeaders(),
        });
        return response.data;
      } catch (err) {
        console.error(`Error searching ${entityType}:`, err);
        return [];
      }
    };

    return (
      <MultiEntityMentionsEditor
        ref={ref}
        value={value}
        onChange={onChange}
        placeholder={
          placeholder ||
          "Type @ to mention OKRs, Segments, Propositions, Companies, or Users..."
        }
        onSearchEntities={handleSearchEntities}
        disabled={disabled}
      />
    );
  },
);

MarkdownEditor.displayName = "MarkdownEditor";

export default MarkdownEditor;
